import { apiUrl, functionsAvailable } from "./env";
import { applyLocalPrompt, createLocalRun, normalizeRun } from "./run";
import type { ChangeItem, CompiledDocument, McpCredential, RunForm, RunStatus, WebsiteSuggestion } from "./types";
import { normalizeWebsiteSuggestions } from "./website";

/**
 * FROZEN CONTRACT — the backend is deployed. URLs, methods, payload shapes and response
 * shapes in this file may not change.
 *
 * The `apiUrl` check lives INSIDE each function, never at a call site. Local preview
 * (apiUrl === "") is a first-class runtime, and a screen that forgets to branch would either
 * crash or, worse, appear to save work that never left the browser.
 */

export async function fetchHealth() {
  const response = await fetch(`${apiUrl}/health`);
  return response.ok;
}

export async function fetchRuns() {
  const response = await fetch(`${apiUrl}/runs`);
  if (!response.ok) throw new Error(`API returned ${response.status}`);
  const data = (await response.json()) as { runs: RunStatus[] };
  return Array.isArray(data.runs) ? data.runs : [];
}

export async function createRemoteRun(form: RunForm) {
  const response = await fetch(`${apiUrl}/runs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(form)
  });
  if (!response.ok) throw new Error(`API returned ${response.status}`);
  // A REMOTE RUN RENDERS THE BACKEND'S RUN OR NOTHING. We no longer overwrite the backend's real,
  // QA-validated patches with client-invented plannedChanges — that fabrication meant the operator
  // reviewed and approved fictions while POST /approve ran QA against a change set nobody reviewed.
  // The reviewed change set must EQUAL the validated change set. normalizeRun only fills absent
  // structural fields with empty-safe defaults; it invents no content.
  return normalizeRun((await response.json()) as RunStatus, form);
}

/** Creates a run against the backend when one is configured, and locally when it is not. */
export async function createRun(form: RunForm) {
  return apiUrl ? createRemoteRun(form) : createLocalRun(form);
}

export async function editRemoteRun(
  runId: string,
  prompt: string,
  document: CompiledDocument,
  opts?: { target?: { blockId: string }; research?: boolean }
) {
  const response = await fetch(`${apiUrl}/runs/${runId}/edit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      prompt,
      document,
      ...(opts?.target ? { target: opts.target } : {}),
      ...(opts?.research ? { research: true } : {})
    })
  });
  if (!response.ok) throw new Error(`API returned ${response.status}`);
  return (await response.json()) as RunStatus;
}

/** Applies a prompt edit remotely when a backend is configured, locally when it is not. */
export async function applyPrompt(run: RunStatus, prompt: string, document: CompiledDocument) {
  return apiUrl ? await editRemoteRun(run.runId, prompt, document) : applyLocalPrompt(run, prompt, document);
}

export async function saveReviewDecision(
  runId: string,
  changeId: string,
  verdict: ChangeItem["verdict"],
  extra?: { after?: string; reason?: string; source?: string }
) {
  // The backend's applyReviewDecisions already reads `after`/`reason` per decision — the client
  // used to drop them. Sending `after` is what makes a direct edit persist as this change's text.
  const decision: Record<string, unknown> = { changeId, verdict };
  if (extra?.after != null) decision.after = extra.after;
  if (extra?.reason != null) decision.reason = extra.reason;
  if (extra?.source) decision.source = extra.source;
  const response = await fetch(`${apiUrl}/runs/${runId}/review`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decisions: [decision] })
  });
  const result = (await response.json().catch(() => null)) as RunStatus | { message?: string } | null;
  return { ok: response.ok, result };
}

export async function approveRemoteRun(runId: string) {
  const response = await fetch(`${apiUrl}/runs/${runId}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirm: true })
  });
  const result = (await response.json().catch(() => null)) as RunStatus | { message?: string } | null;
  return { ok: response.ok, result };
}

export function packageUrl(runId: string) {
  return `${apiUrl}/runs/${runId}/package`;
}

/**
 * The full designed document for a run, as HTML — the real 39-page ATLAS note with its own
 * typography and layout, with each editable block tagged `data-block-id` so the Studio can
 * anchor a suggestion to the exact element the compiler edits. Only available with a backend
 * (`apiUrl` set); in local preview there is no canonical source to serve.
 */
export async function fetchRunDocument(runId: string): Promise<string> {
  if (!apiUrl) throw new Error("The full document is served only by the backend.");
  const response = await fetch(`${apiUrl}/runs/${encodeURIComponent(runId)}/document`);
  if (!response.ok) throw new Error(`Document request returned ${response.status}`);
  return await response.text();
}

export async function issueMcpCredential(googleCredential: string) {
  if (!googleCredential) throw new Error("Google credential is required");
  const response = await fetch("/mcp/token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential: googleCredential })
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(payload?.message || `MCP credential issuer returned ${response.status}`);
  }
  return (await response.json()) as McpCredential;
}

// Gemini research lives in a Netlify function: served in production and under `netlify dev`, but
// not by plain `vite dev`. When it is not being served we do not call it at all — a 404 per
// keystroke is console noise, not a fallback strategy. The caller degrades to local candidates.
export async function researchOrganizationWebsite(organization: string, signal?: AbortSignal) {
  if (!functionsAvailable) {
    throw new Error("Website research runs in production or under `npm run dev:functions`");
  }
  const response = await fetch("/.netlify/functions/research-organization", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ organization }),
    signal
  });
  const contentType = response.headers.get("content-type") || "";
  if (!response.ok || !contentType.includes("application/json")) {
    throw new Error(`Website research returned ${response.status}`);
  }
  const payload = (await response.json()) as {
    provider?: string;
    message?: string;
    suggestions?: WebsiteSuggestion[];
  };
  return {
    provider: payload.provider || "Gemini",
    message: payload.message,
    suggestions: normalizeWebsiteSuggestions(payload.suggestions || [], organization)
  };
}
